<template>
  <v-dialog
    :model-value="modelValue"
    fullscreen
    persistent
    class="org-edit-studio-root"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="org-edit-studio" role="dialog" aria-modal="true">
      <header class="org-edit-studio__bar">
        <button
          type="button"
          class="org-edit-studio__back"
          :disabled="saving"
          @click="close"
        >
          <v-icon size="18">mdi-arrow-left</v-icon>
          <span>Back</span>
        </button>

        <div class="org-edit-studio__bar-brand">
          <h1 class="org-edit-studio__title">Edit organisation</h1>
          <p class="org-edit-studio__subtitle">Craft a polished profile your team and callers will trust</p>
        </div>

        <div class="org-edit-studio__bar-actions">
          <v-btn
            variant="text"
            class="text-none dialog-btn dialog-btn--muted"
            :disabled="saving"
            rounded="lg"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="text-none btn-primary-cta"
            prepend-icon="mdi-check"
            :loading="saving || orgLogoUploading"
            :disabled="saving || orgLogoUploading"
            rounded="lg"
            @click="submit"
          >
            Save changes
          </v-btn>
        </div>
      </header>

      <div class="org-edit-studio__shell">
        <aside class="org-edit-studio__rail">
          <div class="org-edit-studio__rail-card">
            <div class="org-studio-logo">
              <OrganizationLogoUpload
                :key="logoUploadKey"
                v-model="form.logo_s3key"
                :disabled="saving"
                @error="$emit('error', $event)"
                @uploading="orgLogoUploading = $event"
              />
            </div>
            <p class="org-edit-studio__preview-name">
              {{ form.org_name || 'Your organisation' }}
            </p>
            <p v-if="form.org_tag_line" class="org-edit-studio__preview-tag">
              {{ form.org_tag_line }}
            </p>
            <p v-else class="org-edit-studio__preview-tag org-edit-studio__preview-tag--muted">
              Add a tag line to stand out
            </p>
          </div>

          <nav class="org-edit-studio__nav" aria-label="Form sections">
            <button
              v-for="item in navItems"
              :key="item.id"
              type="button"
              class="org-edit-studio__nav-btn"
              :class="{ 'org-edit-studio__nav-btn--active': activeSection === item.id }"
              @click="scrollToSection(item.id)"
            >
              <span class="org-edit-studio__nav-icon">
                <v-icon size="17">{{ item.icon }}</v-icon>
              </span>
              <span class="org-edit-studio__nav-text">
                <span class="org-edit-studio__nav-label">{{ item.label }}</span>
                <span class="org-edit-studio__nav-hint">{{ item.hint }}</span>
              </span>
            </button>
          </nav>
        </aside>

        <main ref="mainScroll" class="org-edit-studio__main" @scroll="onMainScroll">
          <v-form
            ref="formRef"
            v-model="isFormValid"
            class="org-edit-studio__form"
            @submit.prevent="submit"
          >
            <section :id="sectionIds.identity" class="org-studio-card">
              <header class="org-studio-card__head">
                <span class="org-studio-card__icon">
                  <v-icon size="20">mdi-badge-account-horizontal-outline</v-icon>
                </span>
                <div>
                  <h2 class="org-studio-card__title">Identity</h2>
                  <p class="org-studio-card__desc">Name and contact identity for your organisation</p>
                </div>
              </header>

              <div class="org-studio-card__body org-studio-card__body--grid-2">
                <label class="org-studio-field org-studio-field--full">
                  <span class="org-studio-field__label">Organisation name</span>
                  <v-text-field
                    v-model="form.org_name"
                    placeholder="e.g. Claude AI"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :rules="nameRules"
                    rounded="lg"
                  />
                </label>

                <label class="org-studio-field">
                  <span class="org-studio-field__label">Contact email</span>
                  <v-text-field
                    v-model="form.org_contact_email"
                    type="email"
                    placeholder="contact@company.com"
                    variant="outlined"
                    density="comfortable"
                    disabled
                    hide-details
                    rounded="lg"
                  />
                </label>

                <label class="org-studio-field org-studio-field--full">
                  <span class="org-studio-field__label">Tag line</span>
                  <v-text-field
                    v-model="form.org_tag_line"
                    placeholder="A memorable one-liner about your brand"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :rules="tagLineRules"
                    :maxlength="ORG_TAG_LINE_MAX"
                    :counter="ORG_TAG_LINE_MAX"
                    rounded="lg"
                  />
                </label>
              </div>
            </section>

            <section :id="sectionIds.about" class="org-studio-card">
              <header class="org-studio-card__head">
                <span class="org-studio-card__icon org-studio-card__icon--violet">
                  <v-icon size="20">mdi-text-box-search-outline</v-icon>
                </span>
                <div>
                  <h2 class="org-studio-card__title">About</h2>
                  <p class="org-studio-card__desc">Tell people what your organisation does</p>
                </div>
              </header>

              <div class="org-studio-card__body">
                <label class="org-studio-field org-studio-field--textarea">
                  <span class="org-studio-field__label">Description</span>
                  <v-textarea
                    v-model="form.org_description"
                    placeholder="Share your story, services, and what makes you unique…"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :rules="descriptionRules"
                    :maxlength="ORG_DESCRIPTION_MAX"
                    :counter="ORG_DESCRIPTION_MAX"
                    :rows="4"
                    no-resize
                    rounded="lg"
                  />
                </label>
              </div>
            </section>

            <section :id="sectionIds.contact" class="org-studio-card">
              <header class="org-studio-card__head">
                <span class="org-studio-card__icon org-studio-card__icon--teal">
                  <v-icon size="20">mdi-map-marker-radius-outline</v-icon>
                </span>
                <div>
                  <h2 class="org-studio-card__title">Contact &amp; location</h2>
                  <p class="org-studio-card__desc">How callers and customers reach you</p>
                </div>
              </header>

              <div class="org-studio-card__body">
                <div class="org-studio-card__body--grid-3 mb-4">
                  <label class="org-studio-field">
                    <span class="org-studio-field__label">Country</span>
                    <v-autocomplete
                      v-model="selectedCountryCode"
                      :items="countryOptions"
                      item-title="label"
                      item-value="code"
                      placeholder="Select country"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      :rules="countryRules"
                      rounded="lg"
                    />
                  </label>

                  <label class="org-studio-field">
                    <span class="org-studio-field__label">Mobile</span>
                    <v-text-field
                      v-model="form.org_contact_number"
                      type="tel"
                      placeholder="Phone number"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      :rules="mobileRules"
                      :maxlength="mobileMaxLength"
                      rounded="lg"
                      @keypress="allowDigitsOnly"
                    />
                  </label>

                  <label class="org-studio-field">
                    <span class="org-studio-field__label">SIP number</span>
                    <v-text-field
                      v-model="form.assigned_sip_number"
                      placeholder="Assigned SIP"
                      variant="outlined"
                      density="comfortable"
                      disabled
                      hide-details
                      rounded="lg"
                    />
                  </label>
                </div>

                <div class="org-studio-card__body--grid-3 mb-4">
                  <label class="org-studio-field">
                    <span class="org-studio-field__label">City</span>
                    <v-text-field
                      v-model="form.org_city"
                      placeholder="City"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      :rules="cityRules"
                      rounded="lg"
                    />
                  </label>

                  <label class="org-studio-field">
                    <span class="org-studio-field__label">State</span>
                    <v-text-field
                      v-model="form.org_state"
                      placeholder="State / region"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      :rules="stateRules"
                      rounded="lg"
                    />
                  </label>

                  <label class="org-studio-field">
                    <span class="org-studio-field__label">Pincode</span>
                    <v-text-field
                      v-model="form.org_pincode"
                      placeholder="Postal code"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      :rules="pincodeRules"
                      :maxlength="selectedCountryCode === 'IN' ? 6 : 10"
                      rounded="lg"
                      @keypress="allowDigitsOnly"
                    />
                  </label>
                </div>

                <label class="org-studio-field">
                  <span class="org-studio-field__label">Address</span>
                  <v-text-field
                    v-model="form.org_address"
                    placeholder="Street address"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :rules="addressRules"
                    rounded="lg"
                  />
                </label>
              </div>
            </section>

            <section :id="sectionIds.preferences" class="org-studio-card">
              <header class="org-studio-card__head">
                <span class="org-studio-card__icon org-studio-card__icon--amber">
                  <v-icon size="20">mdi-tune-variant</v-icon>
                </span>
                <div>
                  <h2 class="org-studio-card__title">Call preferences</h2>
                  <p class="org-studio-card__desc">Control notifications and inbound call rules</p>
                </div>
              </header>

              <div class="org-studio-card__body">
                <OrganizationCallLogNotificationFields
                  v-model:send-call-logs-by-email="form.send_call_logs_by_email"
                  v-model:email-address-rows="form.email_address_rows"
                  v-model:send-call-logs-by-whatsapp="form.send_call_logs_by_whatsapp"
                  v-model:whatsapp-number-rows="form.whatsapp_number_rows"
                  v-model:restrict-incoming-calls="form.restrict_incoming_calls"
                  :disabled="saving"
                />
              </div>
            </section>

            <section :id="sectionIds.businessQueries" class="org-studio-card org-studio-card--last">
              <header class="org-studio-card__head">
                <span class="org-studio-card__icon org-studio-card__icon--teal">
                  <v-icon size="20">mdi-message-text-outline</v-icon>
                </span>
                <div>
                  <h2 class="org-studio-card__title">Business query alerts</h2>
                  <p class="org-studio-card__desc">
                    Choose where new website business queries are sent
                  </p>
                </div>
              </header>

              <div class="org-studio-card__body">
                <OrganizationBusinessQueryNotificationFields
                  v-model:send-business-queries-by-email="form.send_business_queries_by_email"
                  v-model:business-query-email-rows="form.business_query_email_rows"
                  v-model:send-business-queries-by-whatsapp="form.send_business_queries_by_whatsapp"
                  v-model:business-query-whatsapp-rows="form.business_query_whatsapp_rows"
                  :disabled="saving"
                />
              </div>
            </section>
          </v-form>
        </main>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import updateOrganization, {
  buildUpdateOrganizationInput,
  ORG_DESCRIPTION_MAX,
  ORG_TAG_LINE_MAX,
  validateOrgCallLogPreferences,
  validateOrgBusinessQueryPreferences,
} from '@/mixins/Organization/updateOrganization'
import { stringsToItemRows, normalizeMobileDigits } from '@/utils/agentConfiguration'
import OrganizationCountryForm from '@/mixins/Organization/OrganizationCountryForm'
import OrganizationLogoUpload from '@/components/Organizations/OrganizationLogoUpload.vue'
import OrganizationCallLogNotificationFields from '@/components/Organizations/OrganizationCallLogNotificationFields.vue'
import OrganizationBusinessQueryNotificationFields from '@/components/Organizations/OrganizationBusinessQueryNotificationFields.vue'
import sessionGraphqlMixin from '@/mixins/Organization/sessionGraphqlMixin'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { parseApiBoolean } from '@/utils/format'
import { resolveOrgLogoS3Key } from '@/utils/orgLogo'
import '@/styles/organizations/org-details.css'
import '@/styles/organizations/org-edit-studio.css'

const SECTION_IDS = {
  identity: 'org-edit-section-identity',
  about: 'org-edit-section-about',
  contact: 'org-edit-section-contact',
  preferences: 'org-edit-section-preferences',
  businessQueries: 'org-edit-section-business-queries',
}

export default {
  name: 'UpdateOrgDetailsDialog',
  components: {
    OrganizationLogoUpload,
    OrganizationCallLogNotificationFields,
    OrganizationBusinessQueryNotificationFields,
  },
  mixins: [updateOrganization, OrganizationCountryForm, sessionGraphqlMixin],
  props: {
    modelValue: { type: Boolean, default: false },
    organization: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      ORG_TAG_LINE_MAX,
      ORG_DESCRIPTION_MAX,
      sectionIds: SECTION_IDS,
      activeSection: SECTION_IDS.identity,
      navItems: [
        { id: SECTION_IDS.identity, label: 'Identity', hint: 'Name & tag line', icon: 'mdi-badge-account-horizontal-outline' },
        { id: SECTION_IDS.about, label: 'About', hint: 'Description', icon: 'mdi-text-box-search-outline' },
        { id: SECTION_IDS.contact, label: 'Contact', hint: 'Phone & address', icon: 'mdi-map-marker-radius-outline' },
        { id: SECTION_IDS.preferences, label: 'Preferences', hint: 'Call settings', icon: 'mdi-tune-variant' },
        { id: SECTION_IDS.businessQueries, label: 'Business queries', hint: 'Lead alerts', icon: 'mdi-message-text-outline' },
      ],
      form: {
        org_id: '',
        assigned_sip_number: '',
        org_name: '',
        org_tag_line: '',
        org_description: '',
        org_contact_email: '',
        org_contact_number: '',
        org_country_code: '',
        org_address: '',
        org_city: '',
        org_state: '',
        org_pincode: '',
        logo_s3key: '',
        send_call_logs_by_email: true,
        email_address_rows: [],
        send_call_logs_by_whatsapp: false,
        whatsapp_number_rows: [],
        send_business_queries_by_email: false,
        business_query_email_rows: [],
        send_business_queries_by_whatsapp: false,
        business_query_whatsapp_rows: [],
        restrict_incoming_calls: false,
      },
      isFormValid: false,
      saving: false,
      orgLogoUploading: false,
      logoUploadKey: 0,
    }
  },
  computed: {
    ...mapGetters(['sessionContext']),
    tagLineRules() {
      return [
        (v) =>
          !v?.trim() ||
          v.trim().length <= ORG_TAG_LINE_MAX ||
          `Tag line must be ${ORG_TAG_LINE_MAX} characters or less`,
      ]
    },
    descriptionRules() {
      return [
        (v) =>
          !v?.trim() ||
          v.trim().length <= ORG_DESCRIPTION_MAX ||
          `Description must be ${ORG_DESCRIPTION_MAX} characters or less`,
      ]
    },
  },
  watch: {
    modelValue(open) {
      if (!open || !this.organization) return
      this.logoUploadKey += 1
      this.populateFormFromOrganization()
      this.activeSection = SECTION_IDS.identity
      this.$nextTick(() => {
        const main = this.$refs.mainScroll
        if (main) main.scrollTop = 0
      })
    },
    'organization.org_logo_s3_key'(key) {
      if (!this.modelValue) return
      const logoKey = resolveOrgLogoS3Key({ org_logo_s3_key: key })
      if (logoKey && logoKey !== String(this.form.logo_s3key || '').trim()) {
        this.form.logo_s3key = logoKey
        this.logoUploadKey += 1
      }
    },
    selectedCountryCode(newVal, oldVal) {
      this.onCountryCodeChange(newVal, oldVal)
    },
  },
  methods: {
    scrollToSection(id) {
      const el = document.getElementById(id)
      const main = this.$refs.mainScroll
      if (!el || !main) return
      const top =
        el.getBoundingClientRect().top -
        main.getBoundingClientRect().top +
        main.scrollTop -
        16
      main.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      this.activeSection = id
    },
    onMainScroll() {
      const main = this.$refs.mainScroll
      if (!main) return
      const scrollTop = main.scrollTop + 80
      const sections = this.navItems
        .map((item) => ({
          id: item.id,
          top: document.getElementById(item.id)?.offsetTop ?? 0,
        }))
        .sort((a, b) => b.top - a.top)
      const current = sections.find((s) => scrollTop >= s.top)
      if (current) this.activeSection = current.id
    },
    populateFormFromOrganization() {
      const org = this.organization
      this.isPopulatingForm = true

      this.form = {
        org_id: org.org_id || '',
        assigned_sip_number: org.sip_number || org.assigned_sip_number || '',
        org_name: org.org_name || '',
        org_tag_line: (org.org_tag_line || '').slice(0, ORG_TAG_LINE_MAX),
        org_description: (org.org_description || '').slice(
          0,
          ORG_DESCRIPTION_MAX,
        ),
        org_contact_email: org.org_contact_email || '',
        org_contact_number: org.org_contact_number || '',
        org_country_code: org.org_country_code || '',
        org_address: org.org_address || '',
        org_city: org.org_city || '',
        org_state: org.org_state || '',
        org_pincode: org.org_pincode || '',
        logo_s3key: resolveOrgLogoS3Key(org),
        send_call_logs_by_email: parseApiBoolean(
          org.send_call_logs_by_email,
          true,
        ),
        email_address_rows: stringsToItemRows(
          org.email_addresses,
          'email',
        ),
        send_call_logs_by_whatsapp: parseApiBoolean(
          org.send_call_logs_by_whatsapp,
          false,
        ),
        whatsapp_number_rows: stringsToItemRows(
          org.whatsapp_numbers,
          'whatsapp',
        ),
        send_business_queries_by_email: parseApiBoolean(
          org.send_business_queries_by_email,
          false,
        ),
        business_query_email_rows: stringsToItemRows(
          org.business_query_email_addresses,
          'email',
        ),
        send_business_queries_by_whatsapp: parseApiBoolean(
          org.send_business_queries_by_whatsapp,
          false,
        ),
        business_query_whatsapp_rows: stringsToItemRows(
          (Array.isArray(org.business_query_whatsapp_numbers)
            ? org.business_query_whatsapp_numbers
            : []
          ).map((n) => normalizeMobileDigits(n) || String(n ?? '').trim()),
          'whatsapp',
        ),
        restrict_incoming_calls: parseApiBoolean(
          org.restrict_incoming_calls,
          false,
        ),
      }

      this.orgLogoUploading = false
      this.selectedCountryCode = this.resolveCountryCode(org.org_country_code)
      this.syncCountryCodeToForm()
      this.isPopulatingForm = false

      this.$nextTick(async () => {
        await this.$refs.formRef?.resetValidation()
        await this.$refs.formRef?.validate()
      })
    },
    close() {
      this.$emit('update:modelValue', false)
    },
    async submit() {
      const validation = await this.$refs.formRef?.validate()
      if (validation === false || validation?.valid === false) {
        this.$emit('error', {
          status: 'ERROR',
          message: 'Please fix the highlighted fields before saving.',
        })
        return
      }

      const callLogError = validateOrgCallLogPreferences(this.form)
      if (callLogError) {
        this.$emit('error', { status: 'ERROR', message: callLogError })
        return
      }

      const businessQueryError = validateOrgBusinessQueryPreferences(this.form)
      if (businessQueryError) {
        this.$emit('error', { status: 'ERROR', message: businessQueryError })
        return
      }

      this.syncCountryCodeToForm()

      if (this.orgLogoUploading) {
        this.$emit('error', {
          status: 'ERROR',
          message: 'Please wait for the logo upload to finish',
        })
        return
      }

      this.saving = true
      try {
        const response = await this.updateOrganization(
          buildUpdateOrganizationInput({ ...this.form }, this.sessionContext),
        )

        if (isApiSuccess(response)) {
          this.$emit('saved', response)
          this.close()
        } else {
          this.$emit('error', response)
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
